import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PLACEHOLDER_IMAGE } from '../utils/imageProxy';

export function useProperty(id: string) {
  const [property, setProperty] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      // Get published status id
      const { data: publishedStatus } = await supabase
        .from('property_statuses')
        .select('id')
        .eq('name', 'published')
        .single();

      if (!publishedStatus) {
        throw new Error('Published status not found');
      }

      const { data, error: fetchError } = await supabase
        .from('properties')
        .select(`
          *,
          zones(*),
          provinces(*),
          property_images(*),
          property_features(
            feature_id,
            features:feature_id(*)
          ),
          property_types(*),
          property_states(*),
          property_strengths(*)
        `)
        .eq('id', id)
        .eq('status_id', publishedStatus.id)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        // Ottieni documenti dell'immobile (planimetria)
        const { data: documents, error: documentsError } = await supabase
          .from('property_documents')
          .select('*')
          .eq('property_id', id)
          .eq('type', 'planimetria');
          
        if (documentsError) {
          console.error('Error fetching property documents:', documentsError);
        }
        
        // Ordina le immagini: prima quelle primarie, poi per posizione
        const sortedImages = [...(data.property_images || [])].sort((a, b) => {
          // Put primary image first
          if (a.is_primary && !b.is_primary) return -1;
          if (!a.is_primary && b.is_primary) return 1;
          
          // Then sort by position if available
          if (a.position !== undefined && b.position !== undefined) {
            return a.position - b.position;
          }
          
          // Fallback to creation date
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });
        
        // Transform data to include images array
        const transformedData = {
          ...data,
          images: sortedImages.map((img: any) => img.url),
          primaryImage: sortedImages.find((img: any) => img.is_primary)?.url || 
                       sortedImages[0]?.url || PLACEHOLDER_IMAGE,
          features: data.property_features.map((pf: any) => pf.features).filter(Boolean),
          strengths: data.property_strengths.sort((a: any, b: any) => a.order_index - b.order_index),
          // Aggiungi la planimetria se esiste
          planimetria: documents && documents.length > 0 ? documents[0].url : null
        };

        setProperty(transformedData);
      }
    } catch (err: any) {
      console.error('Error fetching property:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { property, loading, error };
}